import {
  collection, doc, getDocs, setDoc, updateDoc, deleteDoc, addDoc, writeBatch, runTransaction, serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase';

// Client delivery portal: admin-side data access. HQ admins have full access via
// firestore.rules; clients only ever read (see tests/rules/portal.test.js).

export const PROJECT_STATUSES = [['active', 'Active'], ['on_hold', 'On hold'], ['complete', 'Complete']];
export const PHASE_STATUSES = [['not_started', 'Not started'], ['in_progress', 'In progress'], ['delivered', 'Delivered']];
export const DELIVERABLE_STATUSES = [['planned', 'Planned'], ['building', 'Building'], ['delivered', 'Delivered']];
export const DOC_CATEGORIES = [['discovery', 'Discovery'], ['design', 'Design'], ['contract', 'Contract'], ['other', 'Other']];

export const projectRef = (id) => doc(db, 'clientProjects', id);
export const sectionCol = (id, section) => collection(db, 'clientProjects', id, section);
export const sectionDoc = (id, section, docId) => doc(db, 'clientProjects', id, section, docId);
export const portalUrl = (id) => `https://hqadmin.agriware.uk/${id}/`;

const touch = (id) => updateDoc(projectRef(id), { updatedAt: serverTimestamp() });
export const toDateInput = (ts) => (ts?.toDate ? ts.toDate().toISOString().slice(0, 10) : '');
export const fromDateInput = (v) => (v ? Timestamp.fromDate(new Date(`${v}T12:00:00`)) : null);

// Sequential, human-readable ids (project-00001) that double as the portal URL.
export async function createProject({ name, clientName }) {
  const existing = await getDocs(collection(db, 'clientProjects'));
  let n = existing.docs.reduce((max, d) => Math.max(max, Number(d.id.replace('project-', '')) || 0), 0) + 1;
  for (;;) {
    const id = `project-${String(n).padStart(5, '0')}`;
    const created = await runTransaction(db, async (tx) => {
      const ref = projectRef(id);
      if ((await tx.get(ref)).exists()) return false;
      tx.set(ref, {
        name, clientName, clientLogoPath: null, status: 'active', portalEnabled: false, unlockedAt: null,
        currentPhaseId: null, createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
      });
      return true;
    });
    if (created) return id;
    n++;
  }
}

export async function updateProject(id, fields) {
  await updateDoc(projectRef(id), { ...fields, updatedAt: serverTimestamp() });
}

export async function addItem(id, section, data) {
  const r = await addDoc(sectionCol(id, section), data);
  await touch(id);
  return r.id;
}
export async function updateItem(id, section, itemId, fields) {
  await updateDoc(sectionDoc(id, section, itemId), fields);
  await touch(id);
}
export async function deleteItem(id, section, itemId) {
  await deleteDoc(sectionDoc(id, section, itemId));
  await touch(id);
}

// Swap the `order` of two items (move up / down).
export async function swapOrder(id, section, a, b) {
  const batch = writeBatch(db);
  batch.update(sectionDoc(id, section, a.id), { order: b.order });
  batch.update(sectionDoc(id, section, b.id), { order: a.order });
  batch.update(projectRef(id), { updatedAt: serverTimestamp() });
  await batch.commit();
}

// in_progress sets currentPhaseId + startedAt; delivered sets deliveredAt and
// clears currentPhaseId if this phase was current.
export async function setPhaseStatus(id, project, phase, status) {
  const batch = writeBatch(db);
  const fields = { status };
  const projectFields = { updatedAt: serverTimestamp() };
  if (status === 'in_progress') {
    if (!phase.startedAt) fields.startedAt = serverTimestamp();
    fields.deliveredAt = null;
    projectFields.currentPhaseId = phase.id;
  } else if (status === 'delivered') {
    fields.deliveredAt = serverTimestamp();
    if (project.currentPhaseId === phase.id) projectFields.currentPhaseId = null;
  } else {
    fields.startedAt = null;
    fields.deliveredAt = null;
    if (project.currentPhaseId === phase.id) projectFields.currentPhaseId = null;
  }
  batch.update(sectionDoc(id, 'phases', phase.id), fields);
  batch.update(projectRef(id), projectFields);
  await batch.commit();
}

export async function setDeliverableStatus(id, item, status) {
  await updateItem(id, 'deliverables', item.id, { status, deliveredAt: status === 'delivered' ? serverTimestamp() : null });
}

export async function uploadProjectFile(id, file, folder) {
  const safe = file.name.replace(/[^\w.\- ]+/g, '_');
  const path = `clientProjects/${id}/${folder}/${Date.now()}-${safe}`;
  await uploadBytes(storageRef(storage, path), file, { contentType: file.type || 'application/octet-stream' });
  return path;
}
export const fileUrl = (path) => getDownloadURL(storageRef(storage, path));
export const deleteFile = (path) => deleteObject(storageRef(storage, path)).catch(() => {});

export async function addDocument(id, { title, category, file, externalUrl }) {
  if (!!file === !!externalUrl) throw new Error('Choose a file to upload or enter a link, not both.');
  const storagePath = file ? await uploadProjectFile(id, file, 'documents') : null;
  return addItem(id, 'documents', { title, category, storagePath, externalUrl: externalUrl || null, addedAt: serverTimestamp() });
}

export async function setProjectLogo(id, file, previousPath) {
  const path = await uploadProjectFile(id, file, 'logo');
  await updateProject(id, { clientLogoPath: path });
  if (previousPath) await deleteFile(previousPath);
}

// Per-client photo shown behind the portal header (under a heavy dark overlay).
export async function setProjectHeaderImage(id, file, previousPath) {
  if (!file.type.startsWith('image/')) throw new Error('Choose an image file (JPEG, PNG or WebP).');
  if (file.size > 8 * 1024 * 1024) throw new Error('That image is over 8 MB. Use a smaller one.');
  const path = await uploadProjectFile(id, file, 'header');
  await updateProject(id, { headerImagePath: path, headerImageFocus: null });
  if (previousPath) await deleteFile(previousPath);
}

export async function removeProjectHeaderImage(id, path) {
  await updateProject(id, { headerImagePath: null, headerImageFocus: null });
  if (path) await deleteFile(path);
}

export { setDoc, serverTimestamp };
